import { lazy, Suspense } from "react";
import { Loader } from "@/components";
import { Navigate, redirect } from "react-router";
import CreateCustomerPage from "@/pages/AIRXPages/CreateCustomer.page";
import TableFiltersContextProvider from "@/common/contexts/Table.context";
import { AccountsLayouts } from "@/layouts";
import { guardCheck } from "@/common/utils";

const Customers = lazy(() => import("@/pages/AIRXPages/Customers.page"));
const Vendors = lazy(() => import("@/pages/AIRXPages/Vendors.page"));
const AIRXUsers = lazy(() => import("@/pages/AIRXPages/AIRXUsers.page"));
const RolesPermissions = lazy(
  () => import("@/pages/AIRXPages/RolesPermissions.page")
);
const CustomerProfile = lazy(
  () => import("@/pages/CustomerPages/CustomerProfile.page")
);
const VendorsProfilePage = lazy(
  () => import("@/pages/VendorsPages/VendorsProfile.page")
);
const CreateNewUserPage = lazy(() => import("@/pages/CreateNewUser.page"));
const CreateCompanyPage = lazy(
  () => import("@/pages/AIRXPages/CreateCompany.page")
);
const CompanyMePage = lazy(() => import("@/pages/CompanyMe.page"));
const accountRoute = [
  {
    path: "accounts",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "account/airx");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <TableFiltersContextProvider>
          <AccountsLayouts />
        </TableFiltersContextProvider>
      </Suspense>
    ),
    children: [
      {
        path: "",
        loader: async ({ request }: { request: Request }) => {
          const isAllowed = guardCheck(request, "accounts/customer");
          if (!isAllowed) {
            throw redirect("/404");
          }
          return request;
        },
        element: <Navigate to="/accounts/customers" replace />,
      },
      {
        path: "customers",
        loader: async ({ request }: { request: Request }) => {
          const isAllowed = guardCheck(request, "accounts/customer");
          if (!isAllowed) {
            throw redirect("/404");
          }
          return request;
        },
        element: (
          <Suspense fallback={<Loader />}>
            <Customers />
          </Suspense>
        ),
      },
      {
        path: "vendors",
        loader: async ({ request }: { request: Request }) => {
          const isAllowed = guardCheck(request, "account/vendors");
          if (!isAllowed) {
            throw redirect("/404");
          }
          return request;
        },
        element: (
          <Suspense fallback={<Loader />}>
            <Vendors />
          </Suspense>
        ),
      },
      {
        path: "airx-users",
        loader: async ({ request }: { request: Request }) => {
          const isAllowed = guardCheck(request, "user/airx");
          if (!isAllowed) {
            throw redirect("/404");
          }
          return request;
        },
        element: (
          <Suspense fallback={<Loader />}>
            <AIRXUsers />
          </Suspense>
        ),
      },
      {
        path: "roles-permissions",
        loader: async ({ request }: { request: Request }) => {
          const isAllowed = guardCheck(request, "roles/permissions");
          if (!isAllowed) {
            throw redirect("/404");
          }
          return request;
        },
        element: (
          <Suspense fallback={<Loader />}>
            <RolesPermissions />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "company/me",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "company/vendor/customer");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <TableFiltersContextProvider>
          <CompanyMePage />
        </TableFiltersContextProvider>
      </Suspense>
    ),
  },
  {
    path: "customers/:customerId",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "accounts/customer");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <TableFiltersContextProvider>
          <CustomerProfile />
        </TableFiltersContextProvider>
      </Suspense>
    ),
  },
  {
    path: "vendors/:vendorId",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "account/vendors");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <TableFiltersContextProvider>
          <VendorsProfilePage />
        </TableFiltersContextProvider>
      </Suspense>
    ),
  },
  {
    path: "company/:companyId/registerNewUser",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "register/newUser");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <CreateNewUserPage />
      </Suspense>
    ),
  },
  {
    path: "company/register",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "company/register");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <CreateCompanyPage />
      </Suspense>
    ),
  },
  {
    path: "company/register/:customerRegisterID",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "company/register");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense>
        <CreateCustomerPage />
      </Suspense>
    ),
  },
];

export default accountRoute;
